import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTransitionedPresence, type ITransitionedPresence } from '../hooks/transition/useTransitionedPresence';
import { useFeatureGate } from '../hooks/useFeatureGates';
import { PATHS } from '../paths';
import { SIDE_PANEL_TRANSITION_MS } from '../utils/sidePanelMotion';

import {
  INITIAL_ALERTS_PRESENTATION,
  reduceAlertsPresentation,
  type AlertsPresentationAction,
  type IAlertsPresentationState,
} from './alertsPresentation';
import {
  getAssistantSheetOrigin,
  withAssistantSheet,
  withAssistantSheetHandoff,
  withoutAssistantSheet,
} from './assistantSheetHistory';

import type { AlertsCenterPresentation } from '../utils/alert/alertsCenterCard';

/** What the side panel seats. The Notification Center is a layer of the assistant pane, not a second occupant. */
export type SidePanelContent = 'assistant';

export { SIDE_PANEL_TRANSITION_MS } from '../utils/sidePanelMotion';

/**
 * Where the assistant pane mounts, by viewport: the docked side column at
 * `xl+`, the full-page `/assistant` route from `md`, and a bottom sheet over
 * the page below `md`. Exactly one site renders the pane at any width — it
 * carries the live WebSocket, and a second mount is evicted by the server.
 */
export type AssistantMountSite = 'column' | 'sheet' | 'route';

/**
 * The viewport breakpoint at which `EntityPaneBody` switches from one to two
 * columns. Shifts to `2xl` while the side panel occupies horizontal space so
 * the narrowed content track does not try to host two columns until there is
 * genuinely room.
 */
export type ContentColumnsBreakpoint = 'xl' | '2xl';

/** An alert handed to the assistant thread, waiting for the pane to open it. */
export interface IPendingAlertExpand {
  alertId: string;
  /**
   * Whether the row was still unread when it was clicked. Carried rather than
   * re-read later because the same click marks the alert read, so by the time
   * the reply renders the stored status no longer answers "was this new to the
   * user" — see `AssistantPaneView`.
   */
  wasUnread: boolean;
}

/** The panel's own state. `isOpen` is the `xl+` column's; the sheet's open state lives in history. */
export interface ISidePanelState {
  isOpen: boolean;
  alerts: IAlertsPresentationState;
}

/**
 * What the shell and the chrome can do to the panel. The alerts actions are
 * the card's own reducer; the two that need the column as well (`showAlerts`,
 * `toggleAlertsCenter`) are composed here, which is why the column and the card
 * share one reducer rather than two state slots.
 */
export type SidePanelAction =
  | { type: 'toggleAssistant' }
  | { type: 'showAssistant' }
  | { type: 'hideAssistant' }
  | { type: 'showAlerts' }
  | { type: 'toggleAlertsCenter' }
  | { type: 'alerts'; action: AlertsPresentationAction };

interface ISidePanelContextValue {
  /** Whether the assistant is showing at this site: the column's own flag, or the sheet's history entry. */
  isOpen: boolean;
  isAlertsEnabled: boolean;
  /** Which site renders the pane at the current viewport. */
  assistantMountSite: AssistantMountSite;
  /** What the panel would render, or `null` when nothing is showable. */
  visible: SidePanelContent | null;
  /**
   * The `xl+` column's presentational presence: `renderValue` retains the
   * occupant through the ~{@link SIDE_PANEL_TRANSITION_MS} exit so `HomeScene`
   * can play the collapse before unmounting, and `stage` names where the width
   * transition is. Lives here rather than in the scene because
   * {@link ContentColumnsBreakpoint} must flip with the column the user *sees*:
   * keying it on `visible` re-lays panes out at animation start, a visible
   * one-frame snap before the width has moved.
   */
  panelPresence: ITransitionedPresence<SidePanelContent>;
  /** `2xl` while the panel consumes content width, else `xl`. */
  columnsBreakpoint: ContentColumnsBreakpoint;
  /** The nav pill: opens the assistant, or closes it when it is open. */
  toggleAssistant: () => void;
  /** Opens the assistant on its thread, leaving the card where it is. */
  showAssistant: () => void;
  /** Closes the assistant; a no-op when it is already closed. */
  hideAssistant: () => void;
  /**
   * An `/alerts` deep link, and the bell wherever it does not toggle: opens the
   * assistant and the full center. Idempotent — a deep link while the center
   * is already full keeps it full and keeps the presentation it will return to.
   */
  showAlerts: () => void;
  /** The floating pill with alerts to review: opens the assistant onto the truncated card from any presentation. */
  showAssistantOntoAlerts: () => void;
  /** Which of its three presentations the Notification Center card is in. */
  alertsPresentation: AlertsCenterPresentation;
  /** The pill was clicked. */
  expandAlertsCenter: () => void;
  /** The chevron on either card: down to the pill. */
  collapseAlertsCenter: () => void;
  /** The truncated card's "View all". */
  viewAllAlertsCenter: () => void;
  /** Open the full center, remembering which composer presentation it came from. */
  openAlertsCenter: () => void;
  /** Escape, or the bell pressed again: back to the presentation the full center came from. */
  closeAlertsCenter: () => void;
  /** The bell: closes the full center when the assistant is showing it, otherwise opens the assistant onto it. */
  toggleAlertsCenter: () => void;
  /** An alert entered the thread — from a card row, or a record page's banner. */
  noteAlertExpanded: () => void;
  /** The user sent a message, from the composer or a suggestion chip. */
  noteUserSent: () => void;
  /** A row with a fix of its own ran that fix instead of opening in the thread. */
  noteRowFixRan: () => void;
  /**
   * The alert the assistant has been asked to open but has not opened yet, or
   * `null` when nothing is waiting. Lives here rather than on a surface
   * because the surface that asks (a record page's banner) is not the pane
   * that has to act, and that pane may not be mounted yet.
   */
  pendingAlertExpand: IPendingAlertExpand | null;
  /**
   * Hand an alert to the assistant thread: opens the assistant and records
   * which alert to open. On the route the caller still has to navigate — see
   * `useOpenAlertInAssistant`, which is what surfaces should call.
   */
  openAlertInAssistant: (alertId: string, wasUnread: boolean) => void;
  /** Clears the pending alert, once the pane has issued its expand. */
  consumePendingExpand: () => void;
}

/**
 * Default reflects "no side panel" — gates off, today's column behavior. Lets
 * `EntityPaneBody` (and its pane Storybook stories) consume
 * {@link useContentColumnsBreakpoint} without being wrapped in the provider.
 */
const DEFAULT_VALUE: ISidePanelContextValue = {
  isOpen: false,
  isAlertsEnabled: false,
  assistantMountSite: 'route',
  visible: null,
  panelPresence: { renderValue: null, stage: 'open', onTransitionEnd: () => {} },
  columnsBreakpoint: 'xl',
  toggleAssistant: () => {},
  showAssistant: () => {},
  hideAssistant: () => {},
  showAlerts: () => {},
  showAssistantOntoAlerts: () => {},
  alertsPresentation: INITIAL_ALERTS_PRESENTATION.presentation,
  expandAlertsCenter: () => {},
  collapseAlertsCenter: () => {},
  viewAllAlertsCenter: () => {},
  openAlertsCenter: () => {},
  closeAlertsCenter: () => {},
  toggleAlertsCenter: () => {},
  noteAlertExpanded: () => {},
  noteUserSent: () => {},
  noteRowFixRan: () => {},
  pendingAlertExpand: null,
  openAlertInAssistant: () => {},
  consumePendingExpand: () => {},
};

const SidePanelContext = React.createContext<ISidePanelContextValue>(DEFAULT_VALUE);

/** A session starts with the column open on the assistant and the card truncated. */
export const INITIAL_SIDE_PANEL_STATE: ISidePanelState = { isOpen: true, alerts: INITIAL_ALERTS_PRESENTATION };

/**
 * The column and the card together. Pure, so the composed moves — the bell
 * toggle especially, which reads both — are unit tests rather than provider
 * choreography. Returns the same object when nothing changes.
 */
export const reduceSidePanel = (state: ISidePanelState, action: SidePanelAction): ISidePanelState => {
  switch (action.type) {
    case 'toggleAssistant':
      return { ...state, isOpen: !state.isOpen };

    case 'showAssistant':
      return state.isOpen ? state : { ...state, isOpen: true };

    case 'hideAssistant':
      return state.isOpen ? { ...state, isOpen: false } : state;

    case 'showAlerts':
      return openOnto(state, reduceAlertsPresentation(state.alerts, 'open'));

    case 'toggleAlertsCenter':
      // "Full" only counts while the column shows it: a stale full presentation
      // behind a closed column must open, not close.
      if (state.isOpen && state.alerts.presentation === 'full') {
        return withAlerts(state, reduceAlertsPresentation(state.alerts, 'close'));
      }
      return openOnto(state, reduceAlertsPresentation(state.alerts, 'open'));

    case 'alerts':
      return withAlerts(state, reduceAlertsPresentation(state.alerts, action.action));

    // no default
  }
};

const withAlerts = (state: ISidePanelState, alerts: IAlertsPresentationState): ISidePanelState =>
  alerts === state.alerts ? state : { ...state, alerts };

const openOnto = (state: ISidePanelState, alerts: IAlertsPresentationState): ISidePanelState =>
  state.isOpen && alerts === state.alerts ? state : { isOpen: true, alerts };

/** `2xl` while the panel occupies content width, else `xl`. */
export const deriveContentColumnsBreakpoint = (hasVisiblePanel: boolean): ContentColumnsBreakpoint =>
  hasVisiblePanel ? '2xl' : 'xl';

export interface IDeriveAssistantMountSiteInput {
  isXl: boolean;
  isMd: boolean;
}

/**
 * Which site renders the assistant pane. Precedence is explicit — `xl` wins,
 * then `md` — so the two `matchMedia` listeners firing in either order across
 * one resize can never yield two sites in a single render.
 */
export const deriveAssistantMountSite = ({ isXl, isMd }: IDeriveAssistantMountSiteInput): AssistantMountSite =>
  isXl ? 'column' : isMd ? 'route' : 'sheet';

interface ISidePanelProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the cross-cutting state for the assistant's panel: where the pane
 * mounts at this viewport ({@link AssistantMountSite}), whether it is open,
 * which of its three presentations the Notification Center card is in (the
 * card is a layer of the assistant pane at every width, so its presentation
 * outlives the pane's mount), and whether the Notification Center is
 * available (`feat-alerts-center`).
 *
 * The column's open flag is in-memory and starts open, so an `xl+` viewer sees
 * the thread on load. The sheet's open flag is the history entry itself
 * (`assistantSheetHistory`): opening pushes an entry that carries it, so Back
 * closes the sheet and any navigation to a new page leaves it behind. The
 * two never write each other — a column closed at `xl` stays closed after a
 * phone-width detour.
 *
 * It also carries the one hand-off into the pane from outside it: the alert a
 * record page's banner asked the assistant to open. That cannot live on the
 * pane, because on the route and the sheet the pane may not be mounted when
 * the click happens.
 *
 * Consumed by `HomeScene` (renders the column or the sheet + reflows content),
 * `MantleAssistantNavButton` and `MainSidebar` (toggle it, reflect it), the
 * `/assistant` and `/alerts` route gates (hand a deep link off to it), and the
 * pane's `useComposerAlertsCenter` (renders and moves the card) via
 * {@link useSidePanel}; and by `EntityPaneBody` via the narrower
 * {@link useContentColumnsBreakpoint}, so a generic layout primitive depends
 * only on "which breakpoint do my columns use".
 */
const SidePanelProvider = ({ children }: ISidePanelProviderProps) => {
  const isAlertsEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const [state, dispatch] = React.useReducer(reduceSidePanel, INITIAL_SIDE_PANEL_STATE);
  // Deliberately outside `ISidePanelState`: that shape is what the layout
  // derivations above are typed against, and an alert waiting to be opened
  // decides nothing about the column.
  const [pendingAlertExpand, setPendingAlertExpand] = React.useState<IPendingAlertExpand | null>(null);

  const isXl = useBreakpoint('xl');
  const isMd = useBreakpoint('md');
  const assistantMountSite = deriveAssistantMountSite({ isXl, isMd });
  const isSheet = assistantMountSite === 'sheet';

  const location = useLocation();
  const navigate = useNavigate();
  const sheetOrigin = getAssistantSheetOrigin(location.state);
  const isSheetOpen = isSheet && sheetOrigin !== null;

  const openSheet = React.useCallback(() => {
    if (sheetOrigin !== null) {
      return;
    }
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { state: withAssistantSheet(location.state, 'pushed') }
    );
  }, [sheetOrigin, navigate, location]);

  // A pushed entry pops back to the page it was opened from; a redirect's
  // entry has no in-app predecessor, so it is rewritten in place instead.
  const closeSheet = React.useCallback(() => {
    if (sheetOrigin === null) {
      return;
    }
    if (sheetOrigin === 'pushed') {
      void navigate(-1);
      return;
    }
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: withoutAssistantSheet(location.state) }
    );
  }, [sheetOrigin, navigate, location]);

  // A sheet open when the viewport widens past `md` hands the assistant to the
  // route, where the pane lives at that width: the entry becomes `/assistant`
  // in place, holding the sheet's origin, so Back still lands on the page the
  // sheet was opened over and narrowing back (the route gate's redirect)
  // reopens the sheet on an entry closing can pop the way it could before.
  const previousSiteRef = React.useRef(assistantMountSite);
  React.useEffect(() => {
    const previousSite = previousSiteRef.current;
    previousSiteRef.current = assistantMountSite;
    if (previousSite === 'sheet' && assistantMountSite === 'route' && sheetOrigin !== null) {
      void navigate(PATHS.ASSISTANT, { replace: true, state: withAssistantSheetHandoff(location.state, sheetOrigin) });
    }
  }, [assistantMountSite, sheetOrigin, navigate, location.state]);

  const toggleAssistant = React.useCallback(() => {
    if (!isSheet) {
      dispatch({ type: 'toggleAssistant' });
      return;
    }
    if (isSheetOpen) {
      closeSheet();
    } else {
      openSheet();
    }
  }, [isSheet, isSheetOpen, openSheet, closeSheet]);
  const showAssistant = React.useCallback(() => {
    if (isSheet) {
      openSheet();
    } else {
      dispatch({ type: 'showAssistant' });
    }
  }, [isSheet, openSheet]);
  const hideAssistant = React.useCallback(() => {
    if (isSheet) {
      closeSheet();
    } else {
      dispatch({ type: 'hideAssistant' });
    }
  }, [isSheet, closeSheet]);
  const showAlerts = React.useCallback(() => {
    if (isSheet) {
      dispatch({ type: 'alerts', action: 'open' });
      openSheet();
    } else {
      dispatch({ type: 'showAlerts' });
    }
  }, [isSheet, openSheet]);
  const showAssistantOntoAlerts = React.useCallback(() => {
    dispatch({ type: 'alerts', action: 'showTruncated' });
    showAssistant();
  }, [showAssistant]);
  const toggleAlertsCenter = React.useCallback(() => {
    if (!isSheet) {
      dispatch({ type: 'toggleAlertsCenter' });
      return;
    }
    if (isSheetOpen && state.alerts.presentation === 'full') {
      dispatch({ type: 'alerts', action: 'close' });
      return;
    }
    dispatch({ type: 'alerts', action: 'open' });
    openSheet();
  }, [isSheet, isSheetOpen, state.alerts.presentation, openSheet]);
  const expandAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'expand' }), []);
  const collapseAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'collapse' }), []);
  const viewAllAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'viewAll' }), []);
  const openAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'open' }), []);
  const closeAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'close' }), []);
  const noteAlertExpanded = React.useCallback(() => dispatch({ type: 'alerts', action: 'alertExpanded' }), []);
  const noteUserSent = React.useCallback(() => dispatch({ type: 'alerts', action: 'userSent' }), []);
  const noteRowFixRan = React.useCallback(() => dispatch({ type: 'alerts', action: 'rowFixRan' }), []);

  const openAlertInAssistant = React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      showAssistant();
      setPendingAlertExpand({ alertId, wasUnread });
    },
    [showAssistant]
  );

  const consumePendingExpand = React.useCallback(() => setPendingAlertExpand(null), []);

  const isOpen = isSheet ? isSheetOpen : state.isOpen;
  const visible: SidePanelContent | null = isOpen ? 'assistant' : null;

  // Presence tracks the column the user sees: `renderValue` retains the
  // occupant through the exit transition. Gated on the column site here (not
  // just in the scene) so a downward crossing drops it instantly — the exit
  // animation must never hold `AssistantPane` mounted into the window where
  // the `/assistant` route or the sheet could mount its WebSocket a second time.
  const isColumn = assistantMountSite === 'column';
  const panelPresence = useTransitionedPresence(isColumn ? visible : null, {
    transitionProperty: 'width',
    durationMs: SIDE_PANEL_TRANSITION_MS,
  });
  const columnOccupiesWidth = isColumn && panelPresence.renderValue !== null;

  const value = React.useMemo<ISidePanelContextValue>(() => {
    return {
      isOpen,
      isAlertsEnabled,
      assistantMountSite,
      visible,
      panelPresence,
      // Keyed on presence, not `visible`: panes reflow when the column actually
      // leaves (or enters) the row, not at the logical toggle a half-second
      // before the width has finished (or started) moving.
      columnsBreakpoint: deriveContentColumnsBreakpoint(columnOccupiesWidth),
      toggleAssistant,
      showAssistant,
      hideAssistant,
      showAlerts,
      showAssistantOntoAlerts,
      alertsPresentation: state.alerts.presentation,
      expandAlertsCenter,
      collapseAlertsCenter,
      viewAllAlertsCenter,
      openAlertsCenter,
      closeAlertsCenter,
      toggleAlertsCenter,
      noteAlertExpanded,
      noteUserSent,
      noteRowFixRan,
      pendingAlertExpand,
      openAlertInAssistant,
      consumePendingExpand,
    };
  }, [
    isOpen,
    isAlertsEnabled,
    assistantMountSite,
    visible,
    panelPresence,
    columnOccupiesWidth,
    toggleAssistant,
    showAssistant,
    hideAssistant,
    showAlerts,
    showAssistantOntoAlerts,
    state.alerts.presentation,
    expandAlertsCenter,
    collapseAlertsCenter,
    viewAllAlertsCenter,
    openAlertsCenter,
    closeAlertsCenter,
    toggleAlertsCenter,
    noteAlertExpanded,
    noteUserSent,
    noteRowFixRan,
    pendingAlertExpand,
    openAlertInAssistant,
    consumePendingExpand,
  ]);

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

export default SidePanelProvider;

/**
 * Reads the side-panel state. Used by the shell (`HomeScene`, to render and
 * place the column or the sheet), the nav toggle button, the bell, the route
 * gates, and the pane's composer card. Outside the provider it returns the
 * closed, gate-off default, so the no-op value is safe in Storybook.
 *
 * @returns The full context value — `isOpen`, `assistantMountSite`, `visible`,
 *   the alerts gate flag, `columnsBreakpoint`, the assistant setters, the
 *   card's presentation and its movers, and the pending-expand hand-off.
 */
export const useSidePanel = (): ISidePanelContextValue => React.useContext(SidePanelContext);

/**
 * Narrow read of just the column breakpoint, for `EntityPaneBody`. Layout-
 * neutral by name so a generic layout primitive depends on "which breakpoint
 * do my columns use," not on what happens to be in the panel.
 *
 * @returns `'2xl'` while the panel occupies content width, otherwise `'xl'`.
 */
export const useContentColumnsBreakpoint = (): ContentColumnsBreakpoint =>
  React.useContext(SidePanelContext).columnsBreakpoint;
